/**
 * Trainer Meeting Tools — Class Chat (Everyone + Private + access settings).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Send, Settings2, User, Users } from "lucide-react";
import {
  fetchChatAccess,
  fetchChatMessages,
  fetchChatThreads,
  patchChatAccess,
  postChatMessage,
  toggleChatLike,
  toggleChatReaction,
} from "./liveCollaborationApi";
import type {
  ChatAccess,
  ChatMessage,
  ChatThreadPeer,
} from "./liveCollaborationTypes";
import { newCollabId, makeChatThreadId } from "./liveCollaborationTypes";
import { ChatMessageActions } from "./ChatMessageActions";
import { persistWithRetry } from "./collabPersist";
import { mergeOlderById, oldestCreatedAt } from "./collabHistory";
import {
  sortByCreatedAtAsc,
  useStickToBottomScroll,
} from "./useStickToBottomScroll";

type Props = {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  messages: ChatMessage[];
  access: ChatAccess | null;
  /** From room collab/bootstrap — skips the 3-call list hydrate. */
  initialThreads?: ChatThreadPeer[];
  /** Wait for room bootstrap attempt before chat REST fallback. */
  hydrateReady?: boolean;
  joinedSince?: string;
  initialPublicHasMore?: boolean;
  initialDmHasMore?: boolean;
  onMessages: (msgs: ChatMessage[]) => void;
  onAccess: (access: ChatAccess) => void;
  onPublishMessage: (msg: ChatMessage) => Promise<void>;
  onRemoveMessage?: (
    messageId: string,
    opts?: { broadcast?: boolean },
  ) => void;
  /** Compact like/reaction fanout (preferred under load). */
  onPublishEngage?: (msg: ChatMessage) => Promise<void>;
  onPublishAccess: (access: ChatAccess) => Promise<void>;
  /** Open DM with this student (e.g. from toast click). */
  focusPeerId?: string | null;
  onFocusPeerHandled?: () => void;
  /** In-room students the trainer can start a private chat with. */
  studentPeers?: Array<{ identity: string; display_name: string }>;
};

function formatTime(iso?: string | null): string {
  if (!iso) return "";
  try {
    // Always IST so every viewer sees the same clock (e.g. 6:44 PM).
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

function accessSummary(a: ChatAccess): string {
  if (!a.students_chat_enabled) return "Students: disabled";
  if (a.students_group_chat_enabled && a.students_can_message_trainer) {
    return "Students: group + private";
  }
  if (a.students_group_chat_enabled) return "Students: group only";
  if (a.students_can_message_trainer) return "Students: private only";
  return "Students: view only";
}

function ChannelBtn({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: "1 1 120px",
        minWidth: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        border: "none",
        borderRadius: 8,
        padding: "8px 10px",
        background: active ? "#2563eb" : "transparent",
        color: active ? "#fff" : "#94a3b8",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export function TrainerChatPanel({
  sessionId,
  trainerId,
  trainerName,
  messages,
  access,
  initialThreads,
  hydrateReady = true,
  joinedSince = "",
  initialPublicHasMore = false,
  initialDmHasMore = false,
  onMessages,
  onAccess,
  onPublishMessage,
  onRemoveMessage,
  onPublishEngage,
  onPublishAccess,
  focusPeerId = null,
  onFocusPeerHandled,
  studentPeers = [],
}: Props) {
  const [channel, setChannel] = useState<"public" | "dm">("public");
  const [peerId, setPeerId] = useState<string | null>(null);
  const [peerQuery, setPeerQuery] = useState("");
  const [peerMenuOpen, setPeerMenuOpen] = useState(false);
  const peerPickerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [threads, setThreads] = useState<ChatThreadPeer[]>(
    () => initialThreads || [],
  );
  const [busy, setBusy] = useState(false);
  const [engageBusyId, setEngageBusyId] = useState("");
  const composerRef = useRef<HTMLDivElement>(null);
  const [sendStacked, setSendStacked] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const engageInFlight = useRef<Set<string>>(new Set());
  const [publicHasMore, setPublicHasMore] = useState(Boolean(initialPublicHasMore));
  const [dmHasMore, setDmHasMore] = useState(Boolean(initialDmHasMore));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);

  useEffect(() => {
    setPublicHasMore(Boolean(initialPublicHasMore));
    setDmHasMore(Boolean(initialDmHasMore));
  }, [initialPublicHasMore, initialDmHasMore, sessionId]);

  const patchLocal = (updated: ChatMessage) => {
    onMessages(
      messagesRef.current.map((m) =>
        m.message_id === updated.message_id ? { ...m, ...updated } : m,
      ),
    );
  };

  const fanoutEngage = (updated: ChatMessage) => {
    if (onPublishEngage) {
      void onPublishEngage(updated);
      return;
    }
    void onPublishMessage(updated);
  };

  const onLikeMessage = async (message: ChatMessage) => {
    if (message.channel !== "public") return;
    const key = `like:${message.message_id}`;
    if (engageInFlight.current.has(key)) return;
    engageInFlight.current.add(key);
    setEngageBusyId(message.message_id);
    // Optimistic UI for snappy feel under load
    const optimistic: ChatMessage = {
      ...message,
      i_liked: !message.i_liked,
      like_count: Math.max(
        0,
        (message.like_count || 0) + (message.i_liked ? -1 : 1),
      ),
    };
    patchLocal(optimistic);
    fanoutEngage(optimistic);
    try {
      const updated = await toggleChatLike({
        sessionId,
        messageId: message.message_id,
        userId: trainerId,
        userName: trainerName,
      });
      patchLocal({ ...message, ...updated });
      // Reconcile peers only if server counts differ from optimistic.
      if (
        Number(updated.like_count || 0) !== Number(optimistic.like_count || 0) ||
        Boolean(updated.i_liked) !== Boolean(optimistic.i_liked)
      ) {
        fanoutEngage({ ...message, ...updated });
      }
    } catch (err) {
      console.error("chat like failed", err);
      patchLocal(message); // rollback
      fanoutEngage(message);
    } finally {
      engageInFlight.current.delete(key);
      setEngageBusyId("");
    }
  };

  const onReactMessage = async (message: ChatMessage, emoji: string) => {
    const key = `react:${message.message_id}`;
    if (engageInFlight.current.has(key)) return;
    engageInFlight.current.add(key);
    setEngageBusyId(message.message_id);
    const prev = message.reactions || [];
    const alreadyThis = prev.some((r) => r.emoji === emoji && r.i_reacted);
    // One reaction per user: clear my prior emoji, then set/toggle the new one.
    let nextReactions = prev
      .map((r) => {
        if (!r.i_reacted) return r;
        const count = Math.max(0, r.count - 1);
        if (count <= 0) return null;
        return { ...r, count, i_reacted: false };
      })
      .filter((r): r is NonNullable<typeof r> => r != null);
    if (!alreadyThis) {
      const existing = nextReactions.find((r) => r.emoji === emoji);
      if (existing) {
        nextReactions = nextReactions.map((r) =>
          r.emoji === emoji
            ? { ...r, count: r.count + 1, i_reacted: true }
            : r,
        );
      } else {
        nextReactions = [...nextReactions, { emoji, count: 1, i_reacted: true }];
      }
    }
    const optimistic: ChatMessage = { ...message, reactions: nextReactions };
    patchLocal(optimistic);
    fanoutEngage(optimistic);
    try {
      const updated = await toggleChatReaction({
        sessionId,
        messageId: message.message_id,
        userId: trainerId,
        userName: trainerName,
        emoji,
      });
      const next = { ...message, ...updated, reactions: updated.reactions || [] };
      patchLocal(next);
      fanoutEngage(next);
    } catch (err) {
      console.error("chat react failed", err);
      patchLocal(message);
      fanoutEngage(message);
    } finally {
      engageInFlight.current.delete(key);
      setEngageBusyId("");
    }
  };

  useEffect(() => {
    if (initialThreads?.length) setThreads(initialThreads);
  }, [initialThreads]);

  useEffect(() => {
    const peer = String(focusPeerId || "").trim();
    if (!peer) return;
    setChannel("dm");
    setPeerId(peer);
    onFocusPeerHandled?.();
  }, [focusPeerId, onFocusPeerHandled]);

  useEffect(() => {
    const el = composerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => setSendStacked(el.clientWidth < 240);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // Prefer room collab/bootstrap; REST fallback only if access missing.
    if (!hydrateReady || access) return;
    let cancelled = false;
    void (async () => {
      try {
        // Join window, one page. Older messages load on scroll-up.
        const sinceQ = joinedSince ? { since: joinedSince } : undefined;
        const [acc, publicPage, threadRows] = await Promise.all([
          fetchChatAccess(sessionId),
          fetchChatMessages({ sessionId, channel: "public", ...sinceQ }),
          fetchChatThreads({ sessionId, trainerId }),
        ]);
        if (cancelled) return;
        onAccess(acc);
        setThreads(threadRows);
        onMessages(publicPage.items);
        setPublicHasMore(publicPage.hasMore);
      } catch (err) {
        console.error("chat hydrate failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, trainerId, access, hydrateReady]);

  useEffect(() => {
    if (channel !== "dm" || !peerId) return;
    const thread = threads.find((t) => t.peer.identity === peerId);
    if (!thread?.thread_id) return;
    let cancelled = false;
    void (async () => {
      try {
        const dmPage = await fetchChatMessages({
          sessionId,
          channel: "dm",
          threadId: thread.thread_id,
          ...(joinedSince ? { since: joinedSince } : {}),
        });
        if (cancelled) return;
        setDmHasMore(dmPage.hasMore);
        if (!dmPage.items.length) return;
        const prev = messagesRef.current;
        const ids = new Set(prev.map((m) => m.message_id));
        const merged = [...prev];
        for (const m of dmPage.items) {
          if (!ids.has(m.message_id)) merged.push(m);
        }
        if (merged.length !== prev.length) onMessages(merged);
      } catch (err) {
        console.error("dm hydrate failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, peerId, sessionId, threads]);

  // All on Private: one page of every saved student thread.
  useEffect(() => {
    if (channel !== "dm" || peerId || !trainerId) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await fetchChatMessages({
          sessionId,
          channel: "dm",
          trainerId,
        });
        if (cancelled) return;
        setDmHasMore(page.hasMore);
        if (!page.items.length) return;
        const prev = messagesRef.current;
        onMessages(
          mergeOlderById(page.items, prev, (m) => m.message_id),
        );
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        const saved = (initialThreads || []).filter((t) => t.thread_id);
        if (cancelled || status !== 400 || !saved.length) {
          console.error("private inbox failed", err);
          return;
        }
        // Older API: one page per saved thread, a few at a time.
        const pages: { items: ChatMessage[]; hasMore: boolean }[] = [];
        try {
          for (let i = 0; i < saved.length; i += 4) {
            if (cancelled) return;
            const chunk = saved.slice(i, i + 4);
            const part = await Promise.all(
              chunk.map((t) =>
                fetchChatMessages({
                  sessionId,
                  channel: "dm",
                  threadId: t.thread_id,
                }),
              ),
            );
            pages.push(...part);
          }
        } catch (fallbackErr) {
          console.error("private inbox failed", fallbackErr);
          return;
        }
        if (cancelled) return;
        const items = pages.flatMap((p) => p.items);
        setDmHasMore(pages.some((p) => p.hasMore));
        if (!items.length) return;
        onMessages(
          mergeOlderById(items, messagesRef.current, (m) => m.message_id),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, peerId, sessionId, trainerId, initialThreads]);

  const selectedThread = useMemo(() => {
    if (!peerId) return undefined;
    const fromThreads = threads.find((t) => t.peer.identity === peerId);
    if (fromThreads) return fromThreads;
    const peer = studentPeers.find((s) => s.identity === peerId);
    if (!peer) return undefined;
    return {
      thread_id: makeChatThreadId(trainerId, peerId),
      peer: {
        identity: peerId,
        role: "student" as const,
        display_name: peer.display_name || peerId,
      },
    };
  }, [threads, studentPeers, peerId, trainerId]);

  const visible = useMemo(() => {
    let filtered: ChatMessage[];
    if (channel === "public") {
      filtered = messages.filter((m) => m.channel === "public");
    } else if (!peerId || !selectedThread) {
      filtered = messages.filter((m) => m.channel === "dm");
    } else {
      filtered = messages.filter(
        (m) => m.channel === "dm" && m.thread_id === selectedThread.thread_id,
      );
    }
    return sortByCreatedAtAsc(filtered);
  }, [channel, messages, peerId, selectedThread]);

  const lastMessageId = visible.length
    ? String(visible[visible.length - 1]?.message_id || "")
    : "";
  const hasMore = channel === "public" ? publicHasMore : dmHasMore;
  const loadOlderRef = useRef<() => void>(() => {});
  const { scrollerRef, bottomRef, onScroll, pinToBottom, restoreHeightRef } =
    useStickToBottomScroll([
      channel,
      peerId,
      lastMessageId,
      visible.length,
    ], {
      onNearTop: () => loadOlderRef.current(),
    });

  const loadOlder = useCallback(() => {
    if (loadingOlderRef.current || !hasMore) return;
    const threadId =
      channel === "dm" ? selectedThread?.thread_id || undefined : undefined;
    const inbox = channel === "dm" && !threadId;
    if (inbox && !trainerId) return;
    const scoped = messagesRef.current.filter((m) => {
      if (channel === "public") return m.channel === "public";
      if (inbox) return m.channel === "dm";
      return m.channel === "dm" && m.thread_id === threadId;
    });
    const before = oldestCreatedAt(scoped, joinedSince);
    if (!before) {
      if (channel === "public") setPublicHasMore(false);
      else setDmHasMore(false);
      return;
    }
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    restoreHeightRef.current = scrollerRef.current?.scrollHeight ?? null;
    void (async () => {
      try {
        const page = await fetchChatMessages({
          sessionId,
          channel,
          threadId: inbox ? undefined : threadId,
          trainerId: inbox ? trainerId : undefined,
          before,
        });
        onMessages(
          mergeOlderById(
            page.items,
            messagesRef.current,
            (m) => m.message_id,
          ),
        );
        if (channel === "public") setPublicHasMore(page.hasMore);
        else setDmHasMore(page.hasMore);
      } catch (err) {
        console.error("load older chat failed", err);
        restoreHeightRef.current = null;
      } finally {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    })();
  }, [
    channel,
    hasMore,
    joinedSince,
    onMessages,
    restoreHeightRef,
    scrollerRef,
    selectedThread?.thread_id,
    sessionId,
    trainerId,
  ]);
  loadOlderRef.current = loadOlder;

  useEffect(() => {
    pinToBottom("auto");
  }, [channel, peerId, pinToBottom]);

  // Merge live threads from incoming DMs
  useEffect(() => {
    const fromMsgs = new Map<string, ChatThreadPeer>();
    for (const m of messages) {
      if (m.channel !== "dm" || !m.thread_id) continue;
      const peer =
        m.sender.identity === trainerId ? m.recipient : m.sender;
      if (!peer?.identity || peer.identity === trainerId) continue;
      fromMsgs.set(peer.identity, {
        thread_id: m.thread_id,
        peer,
      });
    }
    if (!fromMsgs.size) return;
    setThreads((prev) => {
      const map = new Map(prev.map((t) => [t.peer.identity, t]));
      fromMsgs.forEach((t, id) => map.set(id, t));
      return Array.from(map.values());
    });
  }, [messages, trainerId]);

  // Ensure in-room students appear as selectable Private peers (co-trainers too).
  useEffect(() => {
    if (!studentPeers.length) return;
    setThreads((prev) => {
      const map = new Map(prev.map((t) => [t.peer.identity, t]));
      for (const s of studentPeers) {
        const id = String(s.identity || "").trim();
        if (!id || id === trainerId || map.has(id)) continue;
        map.set(id, {
          thread_id: makeChatThreadId(trainerId, id),
          peer: {
            identity: id,
            role: "student",
            display_name: s.display_name || id,
          },
        });
      }
      return Array.from(map.values());
    });
  }, [studentPeers, trainerId]);

  const dmPeerOptions = useMemo(() => {
    const map = new Map<string, ChatThreadPeer>();
    for (const t of threads) {
      const id = String(t.peer.identity || "").trim();
      if (!id || id === trainerId) continue;
      map.set(id, t);
    }
    for (const s of studentPeers) {
      const id = String(s.identity || "").trim();
      if (!id || id === trainerId || map.has(id)) continue;
      map.set(id, {
        thread_id: makeChatThreadId(trainerId, id),
        peer: {
          identity: id,
          role: "student",
          display_name: s.display_name || id,
        },
      });
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.peer.display_name || a.peer.identity).localeCompare(
        String(b.peer.display_name || b.peer.identity),
      ),
    );
  }, [threads, studentPeers, trainerId]);

  const selectedPeerLabel = useMemo(() => {
    if (!peerId) return "";
    const match = dmPeerOptions.find((t) => t.peer.identity === peerId);
    return match?.peer.display_name || peerId;
  }, [dmPeerOptions, peerId]);

  const filteredDmPeers = useMemo(() => {
    if (!peerMenuOpen) return dmPeerOptions;
    const q = peerQuery.trim().toLowerCase();
    if (!q) return dmPeerOptions;
    return dmPeerOptions.filter((t) => {
      const name = String(t.peer.display_name || "").toLowerCase();
      const id = String(t.peer.identity || "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [dmPeerOptions, peerQuery, peerMenuOpen]);

  useEffect(() => {
    if (!peerMenuOpen) {
      setPeerQuery(selectedPeerLabel);
    }
  }, [selectedPeerLabel, peerMenuOpen]);

  useEffect(() => {
    if (!peerMenuOpen) return;
    const onDocDown = (event: MouseEvent) => {
      if (!peerPickerRef.current?.contains(event.target as Node)) {
        setPeerMenuOpen(false);
        setPeerQuery(selectedPeerLabel);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [peerMenuOpen, selectedPeerLabel]);

  const selectPeer = useCallback((identity: string | null) => {
    setPeerId(identity);
    setPeerMenuOpen(false);
    if (!identity) {
      setPeerQuery("");
      return;
    }
    const match = dmPeerOptions.find((t) => t.peer.identity === identity);
    setPeerQuery(match?.peer.display_name || identity);
  }, [dmPeerOptions]);

  const send = () => {
    const body = text.trim();
    if (!body || busy) return;
    if (channel === "dm" && !peerId) return;
    setBusy(true);
    setText("");
    pinToBottom("smooth");

    const messageId = newCollabId("msg");
    const nowIso = new Date().toISOString();
    const optimistic: ChatMessage =
      channel === "public"
        ? {
            message_id: messageId,
            session_id: sessionId,
            channel: "public",
            thread_id: null,
            sender: {
              identity: trainerId,
              role: "trainer",
              display_name: trainerName,
            },
            recipient: null,
            body,
            created_at: nowIso,
            like_count: 0,
            i_liked: false,
            reactions: [],
          }
        : {
            message_id: messageId,
            session_id: sessionId,
            channel: "dm",
            thread_id: makeChatThreadId(trainerId, peerId || ""),
            sender: {
              identity: trainerId,
              role: "trainer",
              display_name: trainerName,
            },
            recipient: {
              identity: peerId || "",
              role: "student",
              display_name: selectedThread?.peer.display_name || "",
            },
            body,
            created_at: nowIso,
            like_count: 0,
            i_liked: false,
            reactions: [],
          };

    // Keep peer chip list in sync when starting a brand-new DM.
    if (channel === "dm" && peerId && selectedThread) {
      setThreads((prev) => {
        if (prev.some((t) => t.peer.identity === peerId)) return prev;
        return [...prev, selectedThread];
      });
    }

    void onPublishMessage(optimistic);
    setBusy(false);

    void persistWithRetry(() =>
      postChatMessage({
        sessionId,
        senderId: trainerId,
        senderName: trainerName,
        senderRole: "trainer",
        body,
        channel,
        peerId: channel === "dm" ? peerId || undefined : undefined,
        peerName:
          channel === "dm"
            ? selectedThread?.peer.display_name || ""
            : undefined,
        peerRole: "student",
        messageId,
      }),
    ).catch((err) => {
      console.error(err);
      onRemoveMessage?.(messageId);
      setText(body);
    });
  };

  const updateAccess = async (partial: Partial<ChatAccess>) => {
    const prev: ChatAccess = access || {
      session_id: sessionId,
      students_chat_enabled: true,
      students_group_chat_enabled: true,
      students_can_message_trainer: true,
    };
    const optimistic: ChatAccess = { ...prev, ...partial, session_id: sessionId };
    try {
      await onPublishAccess(optimistic);
      const next = await patchChatAccess({
        sessionId,
        ...partial,
      });
      await onPublishAccess(next);
    } catch (err) {
      console.error(err);
      try {
        await onPublishAccess(prev);
      } catch {
        /* ignore */
      }
    }
  };

  const currentAccess: ChatAccess = access || {
    session_id: sessionId,
    students_chat_enabled: true,
    students_group_chat_enabled: true,
    students_can_message_trainer: true,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        height: "100%",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "#1a1d26",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            border: "none",
            background: "transparent",
            padding: "10px 12px",
            cursor: "pointer",
            color: "inherit",
            font: "inherit",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              fontWeight: 700,
              color: "#e2e8f0",
              minWidth: 0,
            }}
          >
            <Settings2 size={14} color="#60a5fa" />
            Chat access permissions
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#64748b",
              fontWeight: 600,
              textAlign: "right",
            }}
          >
            {accessSummary(currentAccess)}
          </span>
        </button>
        {settingsOpen ? (
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {(
              [
                {
                  key: "students_chat_enabled" as const,
                  label: "Allow student chat access",
                },
                {
                  key: "students_group_chat_enabled" as const,
                  label: "Allow group chat for students",
                },
                {
                  key: "students_can_message_trainer" as const,
                  label: "Allow private messages to trainer",
                },
              ] as const
            ).map((row) => (
              <label
                key={row.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "#cbd5e1",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(currentAccess[row.key])}
                  disabled={
                    row.key !== "students_chat_enabled" &&
                    !currentAccess.students_chat_enabled
                  }
                  onChange={(e) =>
                    void updateAccess({ [row.key]: e.target.checked })
                  }
                />
                {row.label}
              </label>
            ))}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          padding: 4,
          borderRadius: 10,
          background: "rgba(255,255,255,0.05)",
          flexShrink: 0,
        }}
      >
        <ChannelBtn
          active={channel === "public"}
          label="Everyone"
          icon={<Users size={14} />}
          onClick={() => {
            setChannel("public");
            setPeerId(null);
            setPeerQuery("");
            setPeerMenuOpen(false);
          }}
        />
        <ChannelBtn
          active={channel === "dm"}
          label="Private"
          icon={<User size={14} />}
          onClick={() => setChannel("dm")}
        />
      </div>

      {channel === "dm" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            flexShrink: 0,
            minHeight: 0,
          }}
        >
          <div ref={peerPickerRef} style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                borderRadius: 8,
                border: peerMenuOpen
                  ? "1px solid rgba(37,99,235,0.65)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.25)",
                padding: "0 10px",
              }}
            >
              <Search size={14} color="#64748b" style={{ flexShrink: 0 }} />
              <input
                value={peerQuery}
                onChange={(e) => {
                  setPeerQuery(e.target.value);
                  setPeerMenuOpen(true);
                }}
                onFocus={() => {
                  setPeerMenuOpen(true);
                  if (peerQuery === selectedPeerLabel) {
                    setPeerQuery("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setPeerMenuOpen(false);
                    setPeerQuery(selectedPeerLabel);
                    (e.target as HTMLInputElement).blur();
                  }
                  if (e.key === "Enter" && filteredDmPeers.length === 1) {
                    e.preventDefault();
                    selectPeer(filteredDmPeers[0].peer.identity);
                  }
                }}
                placeholder="Search students to message…"
                aria-label="Search students for private chat"
                aria-expanded={peerMenuOpen}
                aria-controls="trainer-chat-peer-list"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  background: "transparent",
                  color: "#e2e8f0",
                  padding: "8px 0",
                  fontSize: 12,
                  fontWeight: 600,
                  outline: "none",
                }}
              />
              <ChevronDown
                size={14}
                color="#64748b"
                style={{
                  flexShrink: 0,
                  transform: peerMenuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              />
            </div>
            {peerMenuOpen ? (
              <div
                id="trainer-chat-peer-list"
                role="listbox"
                className="trainer-chat-peer-chips"
                style={{
                  position: "absolute",
                  zIndex: 20,
                  left: 0,
                  right: 0,
                  top: "calc(100% + 4px)",
                  maxHeight: 180,
                  overflowY: "auto",
                  overflowX: "hidden",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "#1a1d26",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
                  padding: 4,
                }}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={!peerId}
                  onClick={() => selectPeer(null)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    background: !peerId ? "rgba(37,99,235,0.35)" : "transparent",
                    color: !peerId ? "#fff" : "#94a3b8",
                    cursor: "pointer",
                  }}
                >
                  All students
                </button>
                {filteredDmPeers.length ? (
                  filteredDmPeers.map((t) => {
                    const active = peerId === t.peer.identity;
                    return (
                      <button
                        key={t.thread_id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => selectPeer(t.peer.identity)}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          border: "none",
                          borderRadius: 6,
                          padding: "8px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          background: active
                            ? "rgba(37,99,235,0.35)"
                            : "transparent",
                          color: active ? "#fff" : "#e2e8f0",
                          cursor: "pointer",
                        }}
                      >
                        {t.peer.display_name || t.peer.identity}
                      </button>
                    );
                  })
                ) : (
                  <p
                    style={{
                      margin: 0,
                      padding: "10px 8px",
                      fontSize: 11,
                      color: "#64748b",
                    }}
                  >
                    No students match “{peerQuery.trim()}”
                  </p>
                )}
              </div>
            ) : null}
          </div>
          <div
            className="trainer-chat-peer-chips"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              maxHeight: 72,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              paddingRight: 4,
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
            }}
          >
            <button
              type="button"
              onClick={() => selectPeer(null)}
              style={{
                border: "none",
                borderRadius: 8,
                padding: "4px 8px",
                fontSize: 10,
                fontWeight: 700,
                background: !peerId ? "#2563eb" : "rgba(255,255,255,0.05)",
                color: !peerId ? "#fff" : "#94a3b8",
                cursor: "pointer",
              }}
            >
              All
            </button>
          </div>
          {!dmPeerOptions.length ? (
            <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>
              No students in the session yet. Present students appear here so any
              trainer can start a private chat.
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="trainer-chat-scroller"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          paddingRight: 4,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        {(hasMore || loadingOlder) ? (
          <button
            type="button"
            onClick={loadOlder}
            disabled={loadingOlder}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#64748b",
              fontSize: 11,
              padding: "8px 0",
              cursor: loadingOlder ? "default" : "pointer",
            }}
          >
            {loadingOlder ? "Loading earlier messages…" : "Load earlier messages"}
          </button>
        ) : null}
        {visible.length === 0 ? (
          <p
            style={{
              margin: "24px 0",
              textAlign: "center",
              fontSize: 12,
              color: "#64748b",
            }}
          >
            No messages in this channel.
          </p>
        ) : (
          visible.map((m) => {
            const mine = m.sender.identity === trainerId;
            return (
              <div
                key={m.message_id}
                style={{
                  alignSelf: mine ? "flex-end" : "flex-start",
                  maxWidth: "88%",
                  borderRadius: 10,
                  padding: "8px 10px",
                  background: mine
                    ? "rgba(37,99,235,0.35)"
                    : "rgba(255,255,255,0.06)",
                  color: mine ? "#dbeafe" : "#cbd5e1",
                }}
              >
                {!mine || (channel === "dm" && !peerId) ? (
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748b",
                    }}
                  >
                    {!mine
                      ? m.sender.display_name || "Student"
                      : `To ${m.recipient?.display_name || "student"}`}
                  </p>
                ) : null}
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {m.body}
                </p>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 10,
                    color: "#64748b",
                  }}
                >
                  {formatTime(m.created_at)}
                </p>
                <ChatMessageActions
                  message={m}
                  allowLike={m.channel === "public"}
                  busy={engageBusyId === m.message_id}
                  onLike={() => void onLikeMessage(m)}
                  onReact={(emoji) => void onReactMessage(m, emoji)}
                />
              </div>
            );
          })
        )}
        <div ref={bottomRef} aria-hidden style={{ height: 1, flexShrink: 0 }} />
      </div>

      <div
        ref={composerRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          flexShrink: 0,
          alignItems: "stretch",
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          paddingTop: 4,
          background: "#141820",
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
          placeholder={
            channel === "public"
              ? "Message everyone..."
              : peerId
                ? `Reply to ${selectedThread?.peer.display_name || "student"}...`
                : "Select a student thread..."
          }
          style={{
            flex: sendStacked ? "1 1 100%" : "1 1 160px",
            minWidth: 0,
            width: sendStacked ? "100%" : undefined,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#1a1d26",
            color: "#e2e8f0",
            padding: "10px 12px",
            fontSize: 13,
            outline: "none",
          }}
        />
        <button
          type="button"
          disabled={
            busy ||
            !text.trim() ||
            (channel === "dm" && !peerId && dmPeerOptions.length > 0)
          }
          onClick={() => void send()}
          style={{
            border: "none",
            borderRadius: 10,
            flex: sendStacked ? "1 1 100%" : "0 0 44px",
            width: sendStacked ? "100%" : 44,
            height: sendStacked ? 40 : 44,
            marginLeft: sendStacked ? 0 : "auto",
            background: "#2563eb",
            color: "#fff",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 700,
            opacity:
              busy ||
              !text.trim() ||
              (channel === "dm" && !peerId && dmPeerOptions.length > 0)
                ? 0.4
                : 1,
          }}
        >
          {sendStacked ? (
            "Send"
          ) : (
            <Send size={16} />
          )}
        </button>
      </div>
    </div>
  );
}
